/**
 * Script Manager - Handles all external scripts and page-level modifications
 * This centralizes script loading to avoid performance issues and syntax conflicts
 */

class ScriptManager {
  constructor() {
    this.headerScriptsLoaded = false;
    this.footerScriptsLoaded = false;
  }

  /**
   * Prevent password managers from auto-filling forms
   * Uses multiple techniques to block LastPass, 1Password, etc.
   */
  disablePasswordManagers() {
    // Method 1: Add autocomplete="off" to all form elements
    const addAutocompleteOff = () => {
      document.querySelectorAll('input, textarea, select').forEach(element => {
        if (!element.hasAttribute('autocomplete')) {
          element.setAttribute('autocomplete', 'off');
        }
      });
    };

    // Method 2: Add data attributes that password managers recognize
    const addPasswordManagerAttributes = () => {
      document.querySelectorAll('input, textarea, select').forEach(element => {
        // Prevent password managers from recognizing fields
        element.setAttribute('data-lpignore', 'true');
        element.setAttribute('data-1p-ignore', 'true');
        element.setAttribute('data-bwignore', 'true'); // Bitwarden
        element.setAttribute('data-form-type', 'other');

        // Additional React-specific prevention
        if (element.type !== 'hidden') {
          element.setAttribute('autoComplete', 'new-password'); // React camelCase
          element.setAttribute('data-testid', 'no-autofill'); // Common React testing attribute
        }
      });
    };

    // Method 3: Add CSS to hide password manager icons
    const addPasswordManagerCSS = () => {
      const style = document.createElement('style');
      style.textContent = `
        /* Hide LastPass icons */
        input::-webkit-credentials-auto-fill-button,
        input::-webkit-strong-password-auto-fill-button {
          display: none;
        }

        /* Hide password manager overlays */
        [data-lastpass-icon-root],
        [data-lastpass-root],
        #lp-pom-root,
        .onepassword-extension-icon,
        ._1PasswordExtensionIcon,
        .bitwarden-browser-extension-icon {
          display: none;
        }

        /* Additional input styling to prevent manager detection */
        input[type="text"], input[type="email"], input[type="password"],
        input[type="number"], textarea, select {
          background-image: none;
        }
      `;
      document.head.appendChild(style);
    };

    // Apply immediately
    addAutocompleteOff();
    addPasswordManagerAttributes();
    addPasswordManagerCSS();

    // Re-apply when new elements are added to DOM
    const observer = new MutationObserver(mutations => {
      let shouldReapply = false;
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              // Element node
              if (
                node.matches &&
                (node.matches('input') || node.matches('textarea') || node.matches('select'))
              ) {
                shouldReapply = true;
              } else if (node.querySelectorAll) {
                const inputs = node.querySelectorAll('input, textarea, select');
                if (inputs.length > 0) shouldReapply = true;
              }
            }
          });
        }
      });

      if (shouldReapply) {
        setTimeout(() => {
          addAutocompleteOff();
          addPasswordManagerAttributes();
        }, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    console.log('🔒 Password manager prevention enabled');
  }

  /**
   * Load header scripts (executed early in page lifecycle)
   */
  loadHeaderScripts() {
    if (this.headerScriptsLoaded) return Promise.resolve();

    return new Promise(resolve => {
      // Disable password managers first
      this.disablePasswordManagers();

      // Add Google Analytics if measurement ID is available
      const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
      if (GA_MEASUREMENT_ID) {
        this.addGoogleAnalytics(GA_MEASUREMENT_ID);
      }

      // Add any other header scripts here

      this.headerScriptsLoaded = true;
      console.log('📊 Header scripts loaded');
      resolve();
    });
  }

  /**
   * Load footer scripts (executed after page content is ready)
   */
  loadFooterScripts() {
    if (this.footerScriptsLoaded) return Promise.resolve();

    return new Promise(resolve => {
      // Add any footer scripts here
      // Example additional analytics, chat widgets, etc.

      this.footerScriptsLoaded = true;
      console.log('🚀 Footer scripts loaded');
      resolve();
    });
  }

  /**
   * Initialize all scripts in proper order
   */
  async initialize() {
    try {
      await this.loadHeaderScripts();

      // Load footer scripts after a small delay to ensure DOM is ready
      setTimeout(async () => {
        await this.loadFooterScripts();
      }, 500);
    } catch (error) {
      console.error('Script Manager initialization error:', error);
    }
  }

  /**
   * Add Google Analytics (ready for future use)
   */
  addGoogleAnalytics(measurementId) {
    if (!measurementId) {
      console.warn('Google Analytics measurement ID not provided');
      return;
    }

    // Load gtag script
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(gtagScript);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', measurementId);

    console.log(`📈 Google Analytics loaded with ID: ${measurementId}`);
  }
}

// Create singleton instance
const scriptManager = new ScriptManager();

export default scriptManager;
